import { z } from 'zod';
import { supabase } from '@/shared/supabase/client';
import type { DepotChantiers, FicheChantier } from '../types';
import { parLots, selonIds } from '../lectures';
import { cheminPreuve } from './chemins';
import {
  COLONNES_HISTORIQUE,
  COLONNES_TYPE_DOCUMENT,
  SELECTION_CHANTIER,
  SELECTION_FICHE_CHANTIER,
  SELECTION_RESUME_CHANTIER,
  argumentsAffectation,
  argumentsAjoutEtape,
  filtreHistorique,
  filtreSansArchives,
  ligneChantierDEtape,
  ligneChantierSeul,
  ligneDeSaisieChantier,
  ligneFicheChantier,
  ligneHistorique,
  ligneResumeChantier,
  ligneTypeDocument,
  messageRefus,
  versChantier,
  versEntreeHistorique,
  versFicheChantier,
  versResumeChantier,
} from './lignes-chantiers';
import { seuilArchives, texteRefus } from '@/shared/referentiel/chantier';
import { notifierPartenaire } from './courriels';
import { adresseSignee, deposerPuisEnregistrer, retirerFichiers } from './stockage';

const lignesId = z.array(z.object({ id: z.string() }));
const lignesChemin = z.array(z.object({ chemin: z.string() }));

const TAILLE_HISTORIQUE = 100;

/* Un site ou une entreprise supprimés entre-temps : la clé étrangère refuse. */
const SAISIE = { '23503': 'Cette entreprise ou ce site n’existe plus.' };
/* Un ticket SAV retient son chantier (db/007-sav.sql). */
const AVEC_TICKETS = { '23503': 'Ce chantier a des tickets SAV : supprimez-les avant lui.' };

/**
 * Les actions sur une étape passent par les fonctions de db/003-chantiers.sql
 * et db/005-reserves.sql : la base tranche, avec les messages de chantier.ts.
 */
async function action(
  fonction:
    | 'affecter_etape'
    | 'affecter_chantier'
    | 'retirer_partenaire_chantier'
    | 'demarrer_etape'
    | 'valider_etape'
    | 'rouvrir_etape'
    | 'signaler_reserve'
    | 'lever_reserve'
    | 'soumettre_etape'
    | 'reprendre_envoi'
    | 'refuser_etape'
    | 'ajouter_etape'
    | 'retirer_etape'
    | 'annoncer_etape',
  args: Record<string, unknown>,
  defaut: string
): Promise<void> {
  const { error } = await supabase().rpc(fonction, args);
  if (error) throw new Error(messageRefus(error, defaut));
}

/* Une requête pour toutes les fiches (par paquets, pour la longueur de
   l'adresse), plus le catalogue des documents, lus ensemble. */
const TAILLE_LOT = 100;

async function lireFiches(ids: readonly string[], defaut: string): Promise<FicheChantier[]> {
  const uniques = [...new Set(ids)];
  if (uniques.length === 0) return [];
  const [lots, types] = await Promise.all([
    Promise.all(
      parLots(uniques, TAILLE_LOT).map((lot) =>
        supabase().from('chantiers').select(SELECTION_FICHE_CHANTIER).in('id', lot)
      )
    ),
    supabase().from('types_document').select(COLONNES_TYPE_DOCUMENT),
  ]);
  /* Le catalogue nomme les documents qu'exigent les étapes ; sans lui, on
     montre leurs identifiants plutôt que de ne rien montrer. */
  const typesDocument = types.error ? [] : z.array(ligneTypeDocument).parse(types.data);
  const fiches = lots.flatMap(({ data, error }) => {
    if (error) throw new Error(messageRefus(error, defaut));
    return z
      .array(ligneFicheChantier)
      .parse(data)
      .map((l) => versFicheChantier(l, typesDocument));
  });
  return selonIds(uniques, fiches);
}

export const chantiersSupabase: DepotChantiers = {
  async lister(options) {
    let requete = supabase().from('chantiers').select(SELECTION_RESUME_CHANTIER);
    /* Les archives restent en base : l'index de db/011-archives.sql sert ce filtre. */
    if (options?.avecArchives !== true) {
      requete = requete.or(filtreSansArchives(seuilArchives()));
    }
    const { data, error } = await requete.order('cree_le', { ascending: false });
    if (error) throw new Error(messageRefus(error, 'Les chantiers n’ont pas pu être lus.'));
    return z.array(ligneResumeChantier).parse(data).map(versResumeChantier);
  },

  async lire(id) {
    const [fiche] = await lireFiches([id], 'Le chantier n’a pas pu être lu.');
    return fiche ?? null;
  },

  fiches(ids) {
    return lireFiches(ids, 'Les chantiers n’ont pas pu être lus.');
  },

  async creer(saisie) {
    /* La référence et les cinq étapes sont posées par la base. */
    const { data, error } = await supabase()
      .from('chantiers')
      .insert(ligneDeSaisieChantier(saisie))
      .select(SELECTION_CHANTIER)
      .single();
    if (error) throw new Error(messageRefus(error, 'Le chantier n’a pas pu être créé.', SAISIE));
    return versChantier(ligneChantierSeul.parse(data));
  },

  async modifier(id, saisie) {
    const { data, error } = await supabase()
      .from('chantiers')
      .update(ligneDeSaisieChantier(saisie))
      .eq('id', id)
      .select('id');
    if (error) {
      throw new Error(messageRefus(error, 'Le chantier n’a pas pu être modifié.', SAISIE));
    }
    if (lignesId.parse(data).length === 0) throw new Error('Chantier introuvable.');
  },

  async supprimer(id) {
    /* Les preuves partent avec le chantier (cascade), pas leurs fichiers : on
       relève les chemins avant de supprimer. */
    const preuves = await supabase()
      .from('preuves')
      .select('chemin, etapes_chantier!inner(chantier_id)')
      .eq('etapes_chantier.chantier_id', id);
    if (preuves.error) {
      throw new Error(messageRefus(preuves.error, 'Le chantier n’a pas pu être supprimé.'));
    }
    const chemins = lignesChemin.parse(preuves.data).map((p) => p.chemin);

    const { data, error } = await supabase().from('chantiers').delete().eq('id', id).select('id');
    if (error) {
      throw new Error(messageRefus(error, 'Le chantier n’a pas pu être supprimé.', AVEC_TICKETS));
    }
    if (lignesId.parse(data).length === 0) throw new Error('Chantier introuvable.');
    await retirerFichiers(chemins);
  },

  affecter(etapeId, affectation) {
    return action(
      'affecter_etape',
      argumentsAffectation(etapeId, affectation),
      'L’étape n’a pas pu être affectée.'
    );
  },

  retirerPartenaire(chantierId, partenaireId) {
    return action(
      'retirer_partenaire_chantier',
      { p_chantier: chantierId, p_partenaire: partenaireId },
      'Le partenaire n’a pas pu être retiré du chantier.'
    );
  },

  affecterChantier(chantierId, partenaireId, periode) {
    return action(
      'affecter_chantier',
      {
        p_chantier: chantierId,
        p_partenaire: partenaireId,
        p_debut: periode?.debut ?? null,
        p_fin: periode?.fin ?? null,
      },
      'Le partenaire du chantier n’a pas pu être enregistré.'
    );
  },

  demarrer(etapeId, fin = null, debut = null, heures = null) {
    return action(
      'demarrer_etape',
      {
        p_etape: etapeId,
        p_fin: fin,
        p_debut: debut,
        p_heure_debut: heures?.debut ?? null,
        p_heure_fin: heures?.fin ?? null,
      },
      'L’étape n’a pas pu être démarrée.'
    );
  },

  annoncer(etapeId, debut, fin, heures = null) {
    return action(
      'annoncer_etape',
      {
        p_etape: etapeId,
        p_debut: debut,
        p_fin: fin,
        p_heure_debut: heures?.debut ?? null,
        p_heure_fin: heures?.fin ?? null,
      },
      'Les dates n’ont pas pu être enregistrées.'
    );
  },

  soumettre(etapeId) {
    return action('soumettre_etape', { p_etape: etapeId }, 'L’étape n’a pas pu être envoyée.');
  },

  reprendreEnvoi(etapeId) {
    return action('reprendre_envoi', { p_etape: etapeId }, 'L’envoi n’a pas pu être repris.');
  },

  valider(etapeId) {
    return action('valider_etape', { p_etape: etapeId }, 'L’étape n’a pas pu être validée.');
  },

  async refuser(etapeId, refus) {
    /* La phrase lue par le partenaire nomme les documents : on lit le catalogue.
       La base vérifie les motifs eux-mêmes. */
    const types = await supabase().from('types_document').select(COLONNES_TYPE_DOCUMENT);
    const catalogue = types.error ? [] : z.array(ligneTypeDocument).parse(types.data);
    await action(
      'refuser_etape',
      {
        p_etape: etapeId,
        p_motifs: refus.motifs,
        p_commentaire: refus.commentaire.trim(),
        p_motif: texteRefus(refus, catalogue),
      },
      'L’étape n’a pas pu être refusée.'
    );
    return notifierPartenaire(etapeId);
  },

  rouvrir(etapeId, motif) {
    return action(
      'rouvrir_etape',
      { p_etape: etapeId, p_motif: motif.trim() },
      'L’étape n’a pas pu être rouverte.'
    );
  },

  ajouterEtape(chantierId, saisie) {
    return action(
      'ajouter_etape',
      argumentsAjoutEtape(chantierId, saisie),
      'L’étape n’a pas pu être ajoutée.'
    );
  },

  retirerEtape(etapeId) {
    return action('retirer_etape', { p_etape: etapeId }, 'L’étape n’a pas pu être retirée.');
  },

  signalerReserve(etapeId, texte) {
    return action(
      'signaler_reserve',
      { p_etape: etapeId, p_texte: texte.trim() },
      'La réserve n’a pas pu être signalée.'
    );
  },

  leverReserve(etapeId) {
    return action('lever_reserve', { p_etape: etapeId }, 'La réserve n’a pas pu être levée.');
  },

  async ajouterPreuve(etapeId, fichier, type) {
    /* Le chantier fait partie du chemin : les règles de Storage le vérifient.
       Le statut évite d'envoyer un fichier que la base refuserait ensuite
       (proteger_preuves) ; la base reste juge. */
    const etape = await supabase()
      .from('etapes_chantier')
      .select('chantier_id, statut')
      .eq('id', etapeId)
      .maybeSingle();
    if (etape.error) {
      throw new Error(messageRefus(etape.error, 'La preuve n’a pas pu être enregistrée.'));
    }
    if (etape.data === null) throw new Error('Étape introuvable.');
    const { chantier_id: chantierId, statut } = ligneChantierDEtape.parse(etape.data);
    if (statut === 'validee') {
      throw new Error('Cette étape est validée : rouvrez-la pour ajouter une preuve.');
    }

    const chemin = cheminPreuve(chantierId, etapeId, fichier.name, crypto.randomUUID());
    await deposerPuisEnregistrer(chemin, fichier, () =>
      supabase().from('preuves').insert({
        etape_id: etapeId,
        type,
        nom: fichier.name,
        taille: fichier.size,
        type_mime: fichier.type,
        chemin,
      })
    );
  },

  async supprimerPreuve(id) {
    const { data, error } = await supabase().from('preuves').delete().eq('id', id).select('chemin');
    if (error) throw new Error(messageRefus(error, 'La preuve n’a pas pu être supprimée.'));
    const chemins = lignesChemin.parse(data).map((p) => p.chemin);
    /* L'accès par ligne ne lève pas d'erreur : il ne supprime rien. Pour un
       partenaire, c'est une preuve déposée par un autre. Une étape validée,
       elle, refuse avec son propre message (proteger_preuves). */
    if (chemins.length === 0) {
      throw new Error('Cette preuve n’existe plus, ou elle a été déposée par quelqu’un d’autre.');
    }
    await retirerFichiers(chemins);
  },

  adressePreuve(chemin) {
    return adresseSignee(chemin);
  },

  async historique(chantierId) {
    /* Les identifiants d'étapes rattachent les preuves, même supprimées. Le
       journal n'est lisible que du bureau : un partenaire reçoit une liste vide. */
    const etapes = await supabase()
      .from('etapes_chantier')
      .select('id')
      .eq('chantier_id', chantierId);
    if (etapes.error) {
      throw new Error(messageRefus(etapes.error, 'L’historique n’a pas pu être lu.'));
    }
    const etapeIds = lignesId.parse(etapes.data).map((e) => e.id);

    const { data, error } = await supabase()
      .from('journal')
      .select(COLONNES_HISTORIQUE)
      .or(filtreHistorique(chantierId, etapeIds))
      .order('horodatage', { ascending: false })
      .order('id', { ascending: false })
      .limit(TAILLE_HISTORIQUE);
    if (error) throw new Error(messageRefus(error, 'L’historique n’a pas pu être lu.'));
    return z.array(ligneHistorique).parse(data).map(versEntreeHistorique);
  },
};
